import { Navigate, Route, Routes } from "react-router-dom";
import { useEffect } from "react";
import Login from "./pages/Authentication/Login.tsx";
import { AuthProvider, useAuth } from "./provider/AuthProvider.tsx";
import { routesConfig } from "./constants/routesConfig.ts";
import AdminRoutes from "./components/Routes/AdminRoutes.tsx";
import PrivateRoutes from "./components/Routes/PrivateRoutes.tsx";
import ErrorPage from "./pages/Pages/ErrorPage.tsx";
import flagsmith from "flagsmith";
import { FlagsmithProvider } from "flagsmith/react";
import useDecimalGuard from "./hooks/useDecimalGuard.tsx";
import SessionTimeoutMonitor from "./components/SessionTimeoutMonitor/SessionTimeoutMonitor.tsx";
import { CalculatorRedirect } from "./pages/Integrations/Lifecycle/calculatorHandoff.tsx";

const ADMIN_ROUTE_PREFIXES = [
  "/settings",
  "/system",
  "/references/kfn-report",
  "/references/reported-insurances",
];

const isAdminRoute = (path: string) =>
  ADMIN_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));

const FlagsmithIdentifier = () => {
  const { userID } = useAuth();

  useEffect(() => {
    if (!userID) return;
    flagsmith
      .identify(userID)
      .then(() =>
        console.log("[Flagsmith] Current flags:", flagsmith.getAllFlags())
      )
      .catch((err) =>
        console.error("[Flagsmith] Failed to identify user:", err)
      );
  }, [userID]);

  return null;
};

const App = () => {
  useDecimalGuard();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) =>
      event.preventDefault();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return (
    <AuthProvider>
      <SessionTimeoutMonitor />
      <FlagsmithProvider
        options={{
          // @ts-ignore
          environmentID: import.meta.env.VITE_FLAGSMITH_ENV_ID,
          onError: (err) => console.error("[Flagsmith] Provider error:", err),
        }}
        flagsmith={flagsmith}
      >
        <FlagsmithIdentifier />
        <Routes>
          <Route element={<PrivateRoutes />}>
            {routesConfig
              .filter((route) => !isAdminRoute(route.path))
              .map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<route.component />}
                />
              ))}
            <Route
              path="/integrations/lifecycle"
              element={<Navigate to="/policies/issue" replace />}
            />
            {/* Insurer issuance pages were replaced by the unified lifecycle page;
                the calculator's vehicle and owner are handed over to it. */}
            {["ozk", "dzi", "generali", "levins"].map((insurer) => (
              <Route
                key={insurer}
                path={`/integrations/${insurer}`}
                element={<CalculatorRedirect insurer={insurer} />}
              />
            ))}
          </Route>

          <Route element={<AdminRoutes />}>
            {routesConfig
              .filter((route) => isAdminRoute(route.path))
              .map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<route.component />}
                />
              ))}
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/not-found" element={<ErrorPage />} />
          <Route path="*" element={<ErrorPage />} />
        </Routes>
      </FlagsmithProvider>
    </AuthProvider>
  );
};

export default App;
